import { DEFAULT_PER_PAGE } from "@/constants/pagination";
import {
  PAYROLL_VOUCHER_STATUS_LABELS,
  PayrollVoucherStatusEnum,
} from "@/enum/payroll-voucher-status";
import { format } from "date-fns";
import { StatusFilterKey } from "./types";

export const PRIMARY_HEX: string = "#613F4F";

export const ACCENT_HEX: string = "#F6D040";

export const LINK_BLUE_HEX: string = "#3c7dd9";

export const CYCLE_BANNER_CLASS_NAME: string =
  "rounded-xl border border-[#613F4F]/30 bg-gradient-to-r from-[#613F4F]/5 to-[#F6D040]/10 p-4";

export const ISO_DATE_FORMAT: string = "yyyy-MM-dd";

export const RANGE_DISPLAY_FORMAT: string = "dd 'de' MMM, yyyy";

export const todayISO = (): string => format(new Date(), ISO_DATE_FORMAT);

export const EMPTY_VOUCHER_RANGE = { start: "", end: "" } as const;

export const PAGE_TITLE: string = "Aprovação de Vales RH";

export const PAGE_DESCRIPTION: string =
  "Dashboard do RH para gerenciar aprovações de vales solicitadas pelo Financeiro.";

// Chaves aposentadas: declaradas so para limpar o que ficou gravado nos navegadores; nunca mais sao lidas.
export const LEGACY_DRAFT_STORAGE_KEYS: readonly string[] = [
  "aprova_vales_rh__draft__v1",
  "aprova_vales_rh__draft__v2",
];

export const MAX_COMMENT_LENGTH: number = 500;

export const DECISION_REASON_MIN_LENGTH: number = 10;

export const DEFAULT_VOUCHER_PAGE: number = 1;

export const DEFAULT_VOUCHER_PER_PAGE: number = DEFAULT_PER_PAGE;

export const ALL_STATUS_FILTER = "ALL" as const;

export const STATUS_FILTERS: readonly {
  key: StatusFilterKey;
  label: string;
  inactiveClassName: string;
}[] = [
  {
    key: ALL_STATUS_FILTER,
    label: "Todos",
    inactiveClassName: "bg-white text-gray-600 border-gray-300",
  },
  {
    key: PayrollVoucherStatusEnum.PENDING,
    label: PAYROLL_VOUCHER_STATUS_LABELS[PayrollVoucherStatusEnum.PENDING],
    inactiveClassName: "bg-amber-50 text-amber-800 border-amber-300",
  },
  {
    key: PayrollVoucherStatusEnum.APPROVED,
    label: PAYROLL_VOUCHER_STATUS_LABELS[PayrollVoucherStatusEnum.APPROVED],
    inactiveClassName: "bg-green-50 text-green-800 border-green-300",
  },
  {
    key: PayrollVoucherStatusEnum.REJECTED,
    label: PAYROLL_VOUCHER_STATUS_LABELS[PayrollVoucherStatusEnum.REJECTED],
    inactiveClassName: "bg-red-50 text-red-800 border-red-300",
  },
] as const;

export const ACTIVE_CHIP_CLASS_NAME: string =
  "bg-[#613F4F] text-white border-[#613F4F]";

export const STATUS_BADGE_CLASS_NAME: Record<PayrollVoucherStatusEnum, string> =
  {
    [PayrollVoucherStatusEnum.AWAITING_CLOSING]:
      "bg-slate-100 text-slate-700 border-slate-200",
    [PayrollVoucherStatusEnum.PENDING]:
      "bg-amber-100 text-amber-800 border-amber-200",
    [PayrollVoucherStatusEnum.APPROVED]:
      "bg-green-100 text-green-800 border-green-200",
    [PayrollVoucherStatusEnum.REJECTED]:
      "bg-red-100 text-red-800 border-red-200",
  };

export const COLUMN_TITLES = {
  DATE: "Data",
  NAME: "Nome",
  CPF: "CPF",
  BRANCH: "Filial",
  AMOUNT: "Valor",
  INSTALLMENTS: "Parcelas",
  STATUS: "Status",
  DECISION: "Decisões",
} as const;

export type VoucherSortColumn =
  | "conciliation_closed_at"
  | "conciliation_date"
  | "employee_name"
  | "store_code"
  | "amount"
  | "status";

export type SortDirection = "asc" | "desc";

export const SORT_DIRECTIONS: readonly SortDirection[] = ["asc", "desc"];

export const VOUCHER_SORT_DEFAULT_DESC: Readonly<
  Record<VoucherSortColumn, boolean>
> = {
  conciliation_closed_at: true,
  conciliation_date: true,
  employee_name: false,
  store_code: false,
  amount: true,
  status: false,
};

export const VOUCHER_SORT_COLUMNS: readonly VoucherSortColumn[] = Object.keys(
  VOUCHER_SORT_DEFAULT_DESC,
) as VoucherSortColumn[];

export const SORT_DEFAULT_DESC: Record<string, boolean> = {
  ...VOUCHER_SORT_DEFAULT_DESC,
  decision: false,
};

export const DEFAULT_VOUCHER_SORT: VoucherSortColumn = "conciliation_closed_at";

export const EXPORT_ITEM_LABELS = {
  PAYROLL_DISCOUNT: "Planilha Descontos em Folha",
  CONSOLIDATED_PAYROLL_DISCOUNT: "Consolidada Desconto em Folha",
} as const;

export {
  DEFAULT_PAGE,
  PER_PAGE_OPTIONS as VOUCHER_PER_PAGE_OPTIONS,
} from "@/constants/pagination";

export {
  MAX_INSTALLMENTS,
  MIN_INSTALLMENTS,
} from "@/constants/payroll-installments";

export const FIRST_INSTALLMENT_INDEX: number = 0;

export const COUNT_ONLY_PER_PAGE: number = 1;

export const VOUCHER_PARAMS = {
  START: "start",
  END: "end",
  STATUS: "status",
  PAGE: "page",
  PER_PAGE: "perPage",
  SORT: "sort",
  DIRECTION: "direction",
} as const;

export const STATUS_FILTER_KEYS: readonly StatusFilterKey[] =
  STATUS_FILTERS.map((filter): StatusFilterKey => filter.key);
